import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { AdSpaceOwnerPackageInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpaceOwnerPackageColumns } from './AdSpaceOwnerPackageColumns';
import { AdSpaceOwnerPackagesUpdateDialog } from './AdSpaceOwnerPackagesUpdateDialog';

const AdSpaceOwnerPackagesAdminList: React.FC = () => {
    const [packages, setPackages] = useState<AdSpaceOwnerPackageInterface[]>([]);
    const [pkg, setPkg] = useState<AdSpaceOwnerPackageInterface | null>(null);
    const [packageId, setPackageId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [packageToDelete, setPackageToDelete] = useState<AdSpaceOwnerPackageInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [packageToUpdate, setPackageToUpdate] = useState<AdSpaceOwnerPackageInterface | null>(null);
    const [updateForm, setUpdateForm] = useState<Partial<AdSpaceOwnerPackageInterface>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all packages
    const handleGetAllPackages = async () => {
        setError(null);
        setPkg(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceOwnerPackageInterface[]>>('/adSpaceOwnerPackages');
            if (result.status && result.data) {
                setPackages(result.data);
            } else {
                setError(result.message || 'Failed to fetch packages');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch packages');
        }
    };

    // Fetch package by id
    const handleGetPackageById = async () => {
        setError(null);
        setPackages([]);
        setSuccess(null);
        if (!packageId) return setError('Please enter a Package ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceOwnerPackageInterface>>(`/adSpaceOwnerPackages/${packageId}`);
            if (result.status && result.data) {
                setPkg(result.data);
            } else {
                setError(result.message || 'Failed to fetch package');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch package');
        }
    };

    // Delete package
    const handleDeletePackage = async () => {
        if (!packageToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/adSpaceOwnerPackages/delete/${packageToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Package deleted successfully');
                setDeleteDialogOpen(false);
                setPackageToDelete(null);
                handleGetAllPackages();
            } else {
                setError(result.message || 'Failed to delete package');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete package');
        }
    };

    // Update package
    const handleUpdatePackage = async () => {
        if (!packageToUpdate || !packageToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);

        try {
            const result = await apiRequest<GenericResponseInterface<AdSpaceOwnerPackageInterface>>(
                `/adSpaceOwnerPackages/update/${packageToUpdate.id}`,
                'PUT',
                {
                    ...packageToUpdate,
                    ...updateForm,
                }
            );
            if (result.status) {
                setSuccess(result.message || 'Package updated successfully');
                setUpdateDialogOpen(false);
                setPackageToUpdate(null);
                setUpdateForm({});
                handleGetAllPackages();
            } else {
                setUpdateError(result.message || 'Failed to update package');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update package');
        }
    };

    const columns = getAdSpaceOwnerPackageColumns(
        setPackageToDelete,
        setDeleteDialogOpen,
        setPackageToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllPackages();
    }, []);

    useEffect(() => {
        if (updateDialogOpen && packageToUpdate) {
            setUpdateForm({
                ...packageToUpdate
            });
        }
    }, [updateDialogOpen, packageToUpdate]);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllPackages}>Get All Packages</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetPackageById}>Get Package by ID</Button>
                <TextField
                    label="Package ID"
                    value={packageId}
                    onChange={e => setPackageId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {packages.length > 0 && (
                    <DataGrid
                        rows={packages.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {pkg && (
                    <DataGrid
                        rows={[{ ...pkg, id: pkg.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this package?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeletePackage}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpaceOwnerPackagesUpdateDialog
                open={updateDialogOpen}
                packageToUpdate={packageToUpdate}
                setPackageToUpdate={setPackageToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdatePackage}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpaceOwnerPackagesAdminList;